import { useNavigate } from 'react-router-dom';

/**
 * Employee privacy notice.
 *
 * California requires an employer to tell staff what personal information is
 * collected about them and why (CCPA/CPRA, which has covered employee data
 * since 2023). Precise location is classed as SENSITIVE personal information,
 * and this app records it — so a notice is not optional politeness.
 *
 * It is written for the driver, not for a lawyer: a notice nobody reads
 * protects nobody, and the deterrent value of "we record where you are while
 * you are on a route" only exists if drivers actually know it.
 *
 * Static and part of the app shell, so it opens with no signal. Every statement
 * here describes what the code actually does; if the behavior changes, this
 * page changes with it.
 */
export function PrivacyScreen() {
  const navigate = useNavigate();

  return (
    <main className="screen prose">
      <button className="btn link" onClick={() => navigate(-1)}>← Back</button>
      <h1>Privacy notice</h1>
      <p className="muted small">
        For people who work for JW Express Delivery, LLC. Last updated{' '}
        {new Date().getFullYear()}.
      </p>

      <h2>What this app records about you</h2>
      <ul>
        <li>
          <strong>Your details:</strong> name, work email, phone number, your
          team and badge, and a photo supplied by the office.
        </li>
        <li>
          <strong>Your phone:</strong> which device you signed in from and when
          it was approved. Not your contacts, messages, photos or anything else
          on it.
        </li>
        <li>
          <strong>Where you are — only while a route is in progress.</strong>{' '}
          Roughly every minute, plus at each delivery.
        </li>
        <li>
          <strong>Each delivery:</strong> the time, a photo of the drop-off, the
          name and signature of whoever received it, any barcode, and how far
          from the building you were.
        </li>
      </ul>

      <h2>When location is <em>not</em> recorded</h2>
      <p>
        Nothing is recorded before you accept a route, after the route is
        finished, or once you sign out. This is enforced by the system itself,
        not by a setting anyone has to remember — if there is no live route, the
        office receives nothing.
      </p>

      <h2>Why</h2>
      <p>
        To prove deliveries happened, to meet the delivery deadlines our clients
        set, to keep the chain of custody for medical shipments, and to confirm
        that the driver assigned to a route is the person running it.
      </p>

      <h2>How long it is kept</h2>
      <ul>
        <li><strong>Location trails:</strong> 90 days, then deleted automatically.</li>
        <li><strong>Delivery records and proof photos:</strong> 3 years.</li>
        <li><strong>Your profile photo:</strong> while you work here.</li>
      </ul>

      <h2>Who can see it</h2>
      <p>
        JW Express Delivery staff. A client may be shown the delivery evidence
        for their own deliveries if they query one. It is not sold, and it is not
        shared with anyone else except where the law requires it.
      </p>

      <h2>No patient information</h2>
      <p>
        This app holds hospital names, driver information and delivery records.
        It never holds patient data — that stays sealed in the totes you carry.
      </p>

      <h2>Your rights</h2>
      <p>
        You can ask what we hold about you and ask us to correct anything that is
        wrong. Speak to your manager, or use the numbers under{' '}
        <strong>Need help</strong>.
      </p>
    </main>
  );
}
